import { actions as CTAPromptActions } from 'aria-viewer/ctaPrompt'
import { TourActions } from 'aria-viewer/tour/constants'
import CTAPromptTypes from 'aria-viewer/tour/constants/CTAPromptTypes'
import ModalContentTypes from 'aria-viewer/modal/constants/ModalContentTypes'
import { type as BatchType } from 'redux-batch-middleware'
import { DISABLE_ACT_PROMPT } from 'aria-viewer/tour/constants/queryConstants'
import { distinctUntilChanged } from 'rxjs/operators'

import 'rxjs/add/operator/delay'
import 'rxjs/add/operator/mapTo'
import 'rxjs/add/operator/take'
import 'rxjs/add/operator/skip'
import 'rxjs/add/operator/takeUntil'
import 'rxjs/add/operator/takeWhile'
import 'rxjs/add/operator/filter'
import 'rxjs/add/operator/skipWhile'
import 'rxjs/add/operator/concat'
import 'rxjs/add/operator/startWith'
import 'rxjs/add/operator/scan'

import { isReduxBatch } from 'aria-viewer/utils/epicsCommon'
import { Observable, combineLatest } from 'rxjs'
import { isModalOpen, isModalClosed } from 'aria-viewer/utils/epicsCommon'

/**
 * This is the magic number of Interactions to wait for to show CTA
 * Current: 20
 */
const NUM_INTERACTIONS_TO_TRIGGER_ACT_NOW = 20

const actNowFilter = (action, { getState }) => {
    const state = getState()

    const hasTourLoaded =
        state.getIn('tour', 'loading', 'tour') &&
        state.getIn('tour', 'loading', 'location') &&
        state.getIn('tour', 'loading', 'media')

    const isInteraction =
        action.type.indexOf('|A|') == 0 ||
        (isReduxBatch(action) && action.payload.some(element => element.type.indexOf('|A|') == 0))

    const actionButtonHasInitialized = state.getIn(['tour', 'actionButtons', 'initialized'])

    return hasTourLoaded && isInteraction && actionButtonHasInitialized
}

const isSetStop = action => {
    return isReduxBatch(action) && action.payload.some(element => element.type == TourActions.SET_STOP)
}

const isActNowPromptOpen = store =>
    store
        .getState()
        .getIn(['modal', 'modalStack'])
        .some(modalStack => modalStack.content.contentType == ModalContentTypes.CTA_PROMPT)

const isActNowAction = action =>
    isReduxBatch(action) && action.payload.some(element => element.type == CTAPromptActions.OPEN_CTA_PROMPT)

const getInteractionObservable = (action$, store) => {
    // Emit action when stop id is not zero and when it changes thereafter
    const seenNewStop$ = action$
        .filter(isSetStop)
        .distinctUntilChanged()
        .map(action => action.payload.find(element => element.type == TourActions.SET_STOP))
        .startWith({ action: {}, hasSeenNewStop: false, lastIndex: 0 })
        .scan((acc, curr) => ({
            ...acc,
            action: curr,
            hasSeenNewStop: acc.lastIndex != curr.current,
            lastIndex: curr.current,
        }))
        .filter(result => result.hasSeenNewStop)
        .map(result => result.action)

    const modalOpen$ = action$.filter(isModalOpen)

    const interactions$ = Observable.combineLatest(action$, seenNewStop$, actionsStream => actionsStream) // Begin observing actions when a new stop has been seen
        // Observe only tour loaded, cta prompt closed, or registration modal closed
        .filter(action => actNowFilter(action, store))
        /**
         * Cancel observable is they're no action buttons
         */
        .takeWhile(
            _ =>
                store.getState().getIn(['tour', 'actionButtons', 'actions']).length > 0 && // AND if the param ctap is in the Query String.
                store
                    .getState()
                    .get('routing')
                    .location.search.indexOf(DISABLE_ACT_PROMPT) == -1
        )
        .skip(NUM_INTERACTIONS_TO_TRIGGER_ACT_NOW)
        // While user is editing registration then skip
        .skipWhile(
            _ =>
                store
                    .getState()
                    .getIn(['modal', 'modalStack'])
                    .some(modalStack => modalStack.content.type == ModalContentTypes.REGISTRATION) ||
                //OR supplemental media present
                store.getState().getIn(['supplementalMedia', 'visible'])
        )
        // At most only open CTA prompt one time
        .take(1)

    const queueActNow$ = modalOpen$
        .withLatestFrom(interactions$)
        .switchMap(_ => (isActNowPromptOpen(store) ? Observable.never() : action$.filter(isModalClosed)))
        .takeUntil(action$.filter(isActNowAction))

    return Observable.merge(interactions$, queueActNow$)
}

const getTimeBasedObservable = (action$, store) => {
    return (
        Observable.timer(60 * 1000, 2 * 60 * 1000)
            .withLatestFrom(action$, (timerStream, actionStream) => actionStream)
            /**
             * Cancel observable is they're no action buttons
             */
            .takeWhile(
                _ =>
                    store.getState().getIn(['tour', 'actionButtons', 'actions']).length > 0 && // AND if the param ctap is in the Query String.
                    store
                        .getState()
                        .get('routing')
                        .location.search.indexOf(DISABLE_ACT_PROMPT) == -1
            )
            .take(1)
    )
}

const actNowEpic = (action$, store) => {
    const modalOpen$ = action$.filter(isModalOpen)

    const actNow$ = action$
        .ofType(TourActions.MAIN_MEDIA_INITIALIZED)
        .filter(media => media.initialized)
        .distinctUntilChanged()
        .switchMap(_ => {
            // For experiences with less than 2 stops use time base observable. Otherwise use interaction based observable.
            const actNowType$ =
                store.getState().getIn(['tour', 'stops', 'list']).size <= 2
                    ? getTimeBasedObservable(action$, store)
                    : getInteractionObservable(action$, store)

            return actNowType$
                .switchMap(action =>
                    Observable.of(action)
                        //delay 2 seconds before showing.
                        .delay(2000)
                        // Dispatch open CTA Prompt action
                        .mapTo(
                            CTAPromptActions.openCTAPrompt({
                                promptType: CTAPromptTypes.ACT_NOW,
                            })
                        )
                        .takeUntil(modalOpen$)
                )
                .repeat(2)
        })

    return actNow$
}

export default actNowEpic
